import * as XLSX from "xlsx";

/**
 * Parser for the "TRH Data Record" demographic workbook.
 *
 * The sheet is laid out with a two-line header ("Serial No." / "Year" sit one
 * row above the demographic labels) and "Year 2024" style separator rows
 * between each year block, so the columns are matched by their label text
 * rather than by position.
 *
 * Columns split into two groups:
 *   - PATIENT_FIELDS  -> real columns on the patients table
 *   - NOTE_FIELDS     -> merged into a single `notes` value, one "Label: value"
 *                        line per column, e.g.
 *                          Source of Referral: Dr. Mariam Mansoor (AKUH)
 *                          Recommended For: DRP
 */

const normalize = (value) =>
  String(value ?? "")
    .replace(/[\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^"+|"+$/g, "")
    .trim()
    .toLowerCase();

// Placeholders used across the sheet for "no value"
const EMPTY_VALUES = ["n/a", "na", "nil", "none", "-", "--", "—", "."];

const cleanCell = (value, joiner = " ") => {
  if (value === null || value === undefined) return "";

  return String(value)
    .split(/[\r\n]+/)
    .map((part) =>
      part
        .replace(/\s+/g, " ")
        .replace(/^"+|"+$/g, "")
        .trim()
    )
    .filter((part) => part && !EMPTY_VALUES.includes(part.toLowerCase()))
    .join(joiner)
    .replace(/\s*[,;]\s*$/, "")
    .trim();
};

// Ordered: the first matching rule wins, so the more specific labels
// ("Relation with Primary Caregiver") are tested before the looser ones.
export const PATIENT_FIELDS = [
  { key: "record_year", label: "Year", test: (h) => h === "year" },
  { key: "name", label: "Name", test: (h) => h === "name" || h.startsWith("name of") || h === "client name" },
  { key: "age", label: "Age", test: (h) => h === "age" },
  { key: "gender", label: "Gender", test: (h) => h === "gender" || h === "sex" },
  {
    key: "primary_caregiver_relation",
    label: "Relation with Primary Caregiver",
    test: (h) => h.includes("relation"),
  },
  {
    key: "primary_care_giver",
    label: "Primary Care Giver",
    test: (h) => h.includes("care giver") || h.includes("caregiver"),
  },
  { key: "marital_status", label: "Marital Status", test: (h) => h.includes("marital") },
  { key: "religion", label: "Religion", test: (h) => h.includes("religion") },
  { key: "nationality", label: "Nationality", test: (h) => h.includes("nationality") },
  { key: "education", label: "Education", test: (h) => h.includes("education") },
  { key: "occupation", label: "Occupation", test: (h) => h.includes("occupation") },
  {
    key: "socio_economic_status",
    label: "Socio-Economic Status",
    test: (h) => h.includes("socio"),
  },
  { key: "address", label: "Address", test: (h) => h.includes("address") },
];

/**
 * `multi` marks columns where a line break separates two distinct values
 * (a second admission, a second date). Everywhere else a line break is just
 * a wrapped phrase — "Resedential Rehab \n Program" — and is joined by a space.
 */
export const NOTE_FIELDS = [
  { key: "source_of_referral", label: "Source of Referral", test: (h) => h.includes("source of referral") },
  { key: "recommended_for", label: "Recommended For", test: (h) => h.includes("recommended") },
  { key: "date_of_admission", label: "Date of Admission", multi: true, test: (h) => h.includes("date of admission") },
  { key: "date_of_discharge", label: "Date of Discharge", multi: true, test: (h) => h.includes("date of discharge") },
  { key: "no_of_admission", label: "No. of Admission", multi: true, test: (h) => h.includes("admission") },
  { key: "length_of_stay", label: "Length of Stay", multi: true, test: (h) => h.includes("length") },
  { key: "assigned_prp", label: "Assigned PRP", test: (h) => h.includes("assigned") },
  { key: "reason_for_discharge", label: "Reason for Discharge", test: (h) => h.includes("reason") },
];

const ALL_FIELDS = [...PATIENT_FIELDS, ...NOTE_FIELDS];

/** Row that carries the demographic labels — needs Name, Age and Gender. */
const findHeaderRowIndex = (rows) => {
  const limit = Math.min(rows.length, 25);

  for (let i = 0; i < limit; i += 1) {
    const cells = (rows[i] || []).map(normalize);
    if (cells.includes("name") && cells.includes("age") && cells.includes("gender")) {
      return i;
    }
  }

  return -1;
};

/**
 * Builds column -> field mapping. Labels can be split across the header row and
 * the row above it because of the merged "Serial No." / "Year" cells.
 */
const buildColumnMap = (rows, headerIndex) => {
  const headerRow = rows[headerIndex] || [];
  const aboveRow = headerIndex > 0 ? rows[headerIndex - 1] || [] : [];
  const width = Math.max(headerRow.length, aboveRow.length);

  const map = {};
  const labels = [];
  const unmatched = [];
  const taken = new Set();

  for (let col = 0; col < width; col += 1) {
    const rawLabel = cleanCell(headerRow[col]) || cleanCell(aboveRow[col]);
    labels[col] = rawLabel;

    if (!rawLabel) continue;

    const normalized = normalize(rawLabel);
    const field = ALL_FIELDS.find((f) => !taken.has(f.key) && f.test(normalized));

    if (field) {
      taken.add(field.key);
      map[col] = field;
    } else if (normalized !== "serial no." && normalized !== "serial no") {
      unmatched.push(rawLabel);
    }
  }

  return { map, labels, unmatched };
};

const TITLES = ["dr", "dr.", "mr", "mr.", "mrs", "mrs.", "ms", "ms.", "miss", "prof", "prof."];

/** "Ahsan Abbas" -> first/last; a leading title stays with the first name. */
export const splitName = (fullName) => {
  const parts = cleanCell(fullName).split(" ").filter(Boolean);

  if (parts.length === 0) return { first_name: "", last_name: "" };
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };

  if (TITLES.includes(parts[0].toLowerCase())) {
    return {
      first_name: `${parts[0]} ${parts[1]}`,
      last_name: parts.slice(2).join(" "),
    };
  }

  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
};

const parseAge = (value) => {
  const match = cleanCell(value).match(/\d{1,3}/);
  if (!match) return null;

  const age = parseInt(match[0], 10);
  return Number.isNaN(age) || age < 0 || age > 150 ? null : age;
};

const parseGender = (value) => {
  const gender = normalize(value);
  if (!gender) return null;
  if (gender.startsWith("m")) return "male";
  if (gender.startsWith("f")) return "female";
  return "other";
};

const parseYear = (value) => {
  const match = cleanCell(value).match(/(19|20)\d{2}/);
  return match ? match[0] : null;
};

/** "Year 2024" separator rows that break the sheet into year blocks. */
const sectionYear = (row) => {
  const first = normalize(row[0]);
  const match = first.match(/^year\s*((?:19|20)\d{2})$/);
  return match ? match[1] : null;
};

/**
 * @param {ArrayBuffer} data raw .xlsx / .xls / .csv contents
 * @returns {{rows: Array, sheetName: string, headerLabels: Array, unmatchedHeaders: Array, skippedRows: number, duplicateCount: number}}
 */
export const parsePatientWorkbook = (data) => {
  const workbook = XLSX.read(data, { type: "array", cellDates: false });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) {
    throw new Error("The workbook has no sheets.");
  }

  const sheet = workbook.Sheets[sheetName];
  const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });

  const headerIndex = findHeaderRowIndex(grid);
  if (headerIndex === -1) {
    throw new Error(
      "Could not find the header row. The sheet must have columns named Name, Age and Gender."
    );
  }

  const { map, labels, unmatched } = buildColumnMap(grid, headerIndex);
  const nameColumn = Object.keys(map).find((col) => map[col].key === "name");

  if (nameColumn === undefined) {
    throw new Error("Could not find a 'Name' column in the sheet.");
  }

  const rows = [];
  const seen = new Set();
  let currentSectionYear = null;
  let skippedRows = 0;
  let duplicateCount = 0;

  for (let i = headerIndex + 1; i < grid.length; i += 1) {
    const gridRow = grid[i] || [];

    const section = sectionYear(gridRow);
    if (section) {
      currentSectionYear = section;
      continue;
    }

    const rawName = cleanCell(gridRow[nameColumn]);
    if (!rawName) {
      if (gridRow.some((cell) => cleanCell(cell))) skippedRows += 1;
      continue;
    }

    const values = {};
    const noteLines = [];

    // Sorted by column so the notes read in the same order as the sheet
    Object.keys(map)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((col) => {
        const field = map[col];
        const isNote = NOTE_FIELDS.some((f) => f.key === field.key);
        const value = cleanCell(gridRow[col], isNote && field.multi ? " / " : " ");

        if (!isNote) {
          values[field.key] = value;
        } else if (value) {
          noteLines.push(`${field.label}: ${value}`);
        }
      });

    const notes = noteLines.join("\n");

    const { first_name: firstName, last_name: lastName } = splitName(rawName);
    const recordYear = parseYear(values.record_year) || currentSectionYear;

    const row = {
      row_number: i + 1,
      full_name: rawName,
      first_name: firstName,
      last_name: lastName || null,
      age: parseAge(values.age),
      gender: parseGender(values.gender),
      record_year: recordYear,
      primary_care_giver: values.primary_care_giver || null,
      primary_caregiver_relation: values.primary_caregiver_relation || null,
      marital_status: values.marital_status || null,
      religion: values.religion || null,
      nationality: values.nationality || null,
      education: values.education || null,
      occupation: values.occupation || null,
      socio_economic_status: values.socio_economic_status || null,
      address: values.address || null,
      notes: notes || null,
    };

    const key = `${normalize(rawName)}|${recordYear || ""}`;
    row.is_duplicate_in_file = seen.has(key);
    if (row.is_duplicate_in_file) duplicateCount += 1;
    seen.add(key);

    rows.push(row);
  }

  return {
    rows,
    sheetName,
    headerLabels: labels.filter(Boolean),
    unmatchedHeaders: unmatched,
    skippedRows,
    duplicateCount,
  };
};

/** Strips the preview-only keys before posting to the API. */
export const toImportPayload = (rows) =>
  rows.map(({ full_name, is_duplicate_in_file, ...row }) => row);

export default parsePatientWorkbook;
